/**
 * Session-level detail view — the unified card for a multi-participant
 * conversation.
 *
 * Where InteractionDetail shows one contact's slice of a conversation
 * (used from Contact → Interactions tab), SessionDetail shows the
 * whole conversation: all participants, all turns color-coded per
 * speaker, merged facts drawn from every contact's extraction, and
 * per-turn Play buttons so the user can hear specific moments.
 *
 * Takes a SessionInteractionGroup (produced upstream by
 * groupInteractionsBySession) so the grouping logic stays in one
 * place and this component is pure render.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import {
  ChevronDown,
  Clock,
  Loader2,
  MapPin,
  MapPinOff,
  Navigation,
  Play,
  Quote,
  UserX,
  Users,
  X,
} from "lucide-react";

import type { Contact } from "@/lib/data";
import type { ContactSignal, ProcessedTurn } from "@/types/voice";
import {
  listSignalsForSession,
  listTurnsForSession,
} from "@/hooks/useSessionProcessor";
import {
  displayNameForContactId,
  formatInteractionDuration,
  formatRelativeTime,
} from "@/utils/interactionDisplay/interactionDisplay";
import { LocationMap } from "@/ui/LocationMap";

import {
  formatParticipants,
  type SessionInteractionGroup,
} from "./sessionInteractions";

interface SessionDetailProps {
  group: SessionInteractionGroup;
  contacts: Contact[];
  onClose: () => void;
}

export function SessionDetail({ group, contacts, onClose }: SessionDetailProps) {
  const hasLocation =
    group.latitude !== null &&
    group.longitude !== null &&
    Number.isFinite(group.latitude) &&
    Number.isFinite(group.longitude);

  const title = formatParticipants(group.participantNames);

  // Loaded turns are used both for the transcript render AND for
  // the participant-count meta (needs unknown-speaker count which
  // only lives on turn rows, not on per-contact interaction rows).
  // Cached here so the transcript block and the header share one
  // fetch instead of loading turns twice.
  const [turns, setTurns] = useState<ProcessedTurn[] | null>(null);

  // Contacts removed via the "× Remove" action in this modal.
  // Filtered out of the participant list render so the header
  // updates instantly without a full refetch. Persists across
  // renders of this specific modal instance.
  const [removedContactIds, setRemovedContactIds] = useState<Set<string>>(
    () => new Set()
  );

  // Phase B.2 — per-person signals loaded once on mount. Grouped
  // under each participant in the "What we learned" block.
  const [signals, setSignals] = useState<ContactSignal[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSignalsForSession(group.sessionId)
      .then((rows) => {
        if (!cancelled) setSignals(rows);
      })
      .catch(() => {
        if (!cancelled) setSignals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [group.sessionId]);

  const visibleParticipants = useMemo(
    () =>
      group.participants.filter(
        (p) => !removedContactIds.has(p.contactId)
      ),
    [group.participants, removedContactIds]
  );

  const unknownSpeakerCount = useMemo(() => {
    if (!turns) return null;
    const labels = new Set<string>();
    for (const t of turns) {
      if (t.contactId) continue;
      if (t.speakerLabel) labels.add(t.speakerLabel);
    }
    return labels.size;
  }, [turns]);

  const contactCount = visibleParticipants.length;
  const totalSpeakerCount =
    unknownSpeakerCount === null
      ? null
      : contactCount + unknownSpeakerCount;

  const handleRemoveContact = async (contactId: string) => {
    try {
      await invoke("plugin:voice|remove_contact_from_session", {
        sessionId: group.sessionId,
        contactId,
      });
      setRemovedContactIds((prev) => {
        const next = new Set(prev);
        next.add(contactId);
        return next;
      });
      // Optimistically clear the removed contact from loaded turns
      // so the transcript updates in place without refetching.
      setTurns((prev) =>
        prev?.map((t) =>
          t.contactId === contactId ? { ...t, contactId: null } : t
        ) ?? prev
      );
    } catch (err) {
      console.warn("remove_contact_from_session failed:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-[9500] flex flex-col bg-background text-foreground">
      <header
        className="flex items-center justify-between border-b border-[rgba(255,255,255,0.06)] px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <div className="flex min-w-0 flex-col">
          <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
            Conversation
          </p>
          <h2 className="text-foreground truncate text-[18px] font-semibold">
            {title}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full border border-[rgba(255,255,255,0.1)] p-2 active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div
        className="flex-1 overflow-y-auto px-5 py-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
      >
        <div className="flex flex-col gap-5">
          <section className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[rgba(255,255,255,0.6)]">
            <MetaChip
              icon={<Clock className="h-3.5 w-3.5" />}
              label={formatRelativeTime(group.createdAt)}
            />
            <MetaChip
              icon={<Navigation className="h-3.5 w-3.5" />}
              label={formatInteractionDuration(group.totalSecondsSpoken)}
            />
            {totalSpeakerCount !== null && totalSpeakerCount > 1 && (
              <MetaChip
                icon={<Users className="h-3.5 w-3.5" />}
                label={
                  unknownSpeakerCount && unknownSpeakerCount > 0
                    ? `${totalSpeakerCount} people (${contactCount} known + ${unknownSpeakerCount} unknown)`
                    : `${totalSpeakerCount} people`
                }
              />
            )}
            {group.locationLabel && (
              <MetaChip
                icon={<MapPin className="h-3.5 w-3.5" />}
                label={group.locationLabel}
              />
            )}
          </section>

          {visibleParticipants.length > 0 && (
            <ParticipantsRow
              participants={visibleParticipants}
              contacts={contacts}
              onRemove={handleRemoveContact}
            />
          )}

          <section>
            <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
              Summary
            </p>
            <p className="text-foreground mt-2 text-[16px] leading-relaxed">
              {group.summary}
            </p>
          </section>

          <FactsBlock group={group} />

          {signals && signals.length > 0 && (
            <SignalsBlock
              signals={signals}
              contacts={contacts}
              removedContactIds={removedContactIds}
            />
          )}

          <TranscriptBlock
            sessionId={group.sessionId}
            contacts={contacts}
            turns={turns}
            onTurnsLoaded={setTurns}
          />

          {hasLocation ? (
            <LocationSection
              latitude={group.latitude as number}
              longitude={group.longitude as number}
              label={group.locationLabel}
            />
          ) : (
            <NoLocationSection />
          )}
        </div>
      </div>
    </div>
  );
}

function MetaChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {icon}
      <span>{label}</span>
    </span>
  );
}

// ── Phase B.2 — per-person signals block ──

interface SignalsBlockProps {
  signals: ContactSignal[];
  contacts: Contact[];
  removedContactIds: Set<string>;
}

function SignalsBlock({
  signals,
  contacts,
  removedContactIds,
}: SignalsBlockProps) {
  // Group by contactId; signals with null contactId (couldn't be
  // attributed) get bundled under a "Someone" header so they aren't
  // silently dropped.
  const grouped = useMemo(() => {
    const map = new Map<string, ContactSignal[]>();
    for (const s of signals) {
      if (s.contactId && removedContactIds.has(s.contactId)) continue;
      const key = s.contactId ?? "__unknown__";
      const list = map.get(key);
      if (list) list.push(s);
      else map.set(key, [s]);
    }
    return map;
  }, [signals, removedContactIds]);

  if (grouped.size === 0) return null;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-[rgba(120,180,255,0.18)] bg-[rgba(120,180,255,0.04)] p-4">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(120,180,255,0.8)]">
        What we learned
      </p>

      <div className="flex flex-col gap-4">
        {Array.from(grouped.entries()).map(([contactId, items]) => {
          const name =
            contactId === "__unknown__"
              ? "Someone (unresolved)"
              : displayNameForContactId(contactId, contacts);
          return (
            <div key={contactId} className="flex flex-col gap-1.5">
              <p className="text-[12px] font-medium text-[rgba(255,255,255,0.85)]">
                {name}
              </p>
              <ul className="flex flex-col gap-1.5">
                {items.map((s) => (
                  <SignalRow key={s.id} signal={s} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SignalRow({ signal }: { signal: ContactSignal }) {
  const typeLabel = signalTypeLabel(signal.signalType);
  return (
    <li className="flex flex-col gap-0.5 border-l border-[rgba(120,180,255,0.3)] pl-3">
      <div className="flex items-baseline gap-2">
        <span className="rounded-full bg-[rgba(120,180,255,0.1)] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.1em] text-[rgba(120,180,255,0.85)]">
          {typeLabel}
        </span>
        <span className="text-[13px] text-[rgba(255,255,255,0.9)]">
          {signal.value}
        </span>
      </div>
      {signal.sourceQuote && (
        <p className="text-[11px] italic leading-snug text-[rgba(255,255,255,0.45)]">
          &ldquo;{signal.sourceQuote}&rdquo;
        </p>
      )}
    </li>
  );
}

function signalTypeLabel(type: string): string {
  switch (type) {
    case "currentJob": return "job";
    case "currentSchool": return "school";
    case "currentCity": return "city";
    case "interest": return "interest";
    case "lifeEvent": return "life event";
    case "preference": return "preference";
    case "relationship": return "relationship";
    case "skill": return "skill";
    case "belief": return "belief";
    default: return type;
  }
}

// ── Participant chips with "× Remove" action ──

interface ParticipantsRowProps {
  participants: { contactId: string }[];
  contacts: Contact[];
  onRemove: (contactId: string) => Promise<void>;
}

function ParticipantsRow({
  participants,
  contacts,
  onRemove,
}: ParticipantsRowProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  return (
    <section>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em] mb-2">
        Participants
      </p>
      <div className="flex flex-wrap gap-1.5">
        {participants.map((p) => {
          const name = displayNameForContactId(p.contactId, contacts);
          const isConfirming = confirming === p.contactId;
          const isBusy = busy === p.contactId;
          return (
            <div
              key={p.contactId}
              className="flex items-center gap-1 rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] pl-2.5 pr-1 py-0.5 text-[12px] text-[rgba(255,255,255,0.75)]"
            >
              <span>{name}</span>
              {isConfirming ? (
                <button
                  type="button"
                  onClick={async () => {
                    setBusy(p.contactId);
                    setConfirming(null);
                    try {
                      await onRemove(p.contactId);
                    } finally {
                      setBusy(null);
                    }
                  }}
                  disabled={isBusy}
                  className="ml-1 flex items-center gap-0.5 rounded-full bg-destructive/30 px-1.5 py-0.5 text-[10px] font-medium text-destructive active:scale-95"
                  aria-label={`Confirm remove ${name}`}
                >
                  {isBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <UserX className="h-3 w-3" />
                  )}
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(p.contactId)}
                  className="flex items-center justify-center rounded-full p-1 text-[rgba(255,255,255,0.4)] active:scale-95"
                  aria-label={`Remove ${name} from this conversation`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {confirming && (
        <p className="text-[11px] text-[rgba(255,120,120,0.8)] mt-2">
          Tap Remove again to un-attribute{" "}
          {displayNameForContactId(confirming, contacts)} from this
          conversation. Their voiceprint and contact stay intact — only
          their turns in THIS session revert to unresolved.
        </p>
      )}
    </section>
  );
}

// ── Merged facts across all participants' extractions ──

function FactsBlock({ group }: { group: SessionInteractionGroup }) {
  const hasAnything =
    group.highlights.length > 0 ||
    group.commitments.length > 0 ||
    group.openQuestions.length > 0 ||
    group.notableQuotes.length > 0 ||
    group.peopleMentioned.length > 0 ||
    group.topics.length > 0;
  if (!hasAnything) return null;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-[rgba(201,168,76,0.12)] bg-[rgba(201,168,76,0.03)] p-4">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(201,168,76,0.7)]">
        What mattered
      </p>

      {group.highlights.length > 0 && <FactList items={group.highlights} />}
      {group.commitments.length > 0 && (
        <FactGroup label="Commitments" items={group.commitments} />
      )}
      {group.openQuestions.length > 0 && (
        <FactGroup label="Left open" items={group.openQuestions} />
      )}
      {group.notableQuotes.length > 0 && (
        <QuotesGroup quotes={group.notableQuotes} />
      )}

      {(group.peopleMentioned.length > 0 || group.topics.length > 0) && (
        <div className="flex flex-col gap-2">
          {group.peopleMentioned.length > 0 && (
            <ChipRow label="People" values={group.peopleMentioned} />
          )}
          {group.topics.length > 0 && (
            <ChipRow label="Topics" values={group.topics} />
          )}
        </div>
      )}

      {group.sentiment && (
        <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.35)]">
          Tone —{" "}
          <span className="text-[rgba(255,255,255,0.6)]">{group.sentiment}</span>
        </p>
      )}
    </section>
  );
}

function FactList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5 text-[14px] leading-relaxed text-[rgba(255,255,255,0.85)]">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-2">
          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[rgba(201,168,76,0.7)]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function FactGroup({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        {label}
      </p>
      <ul className="flex flex-col gap-1 text-[14px] leading-relaxed text-[rgba(255,255,255,0.8)]">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function QuotesGroup({ quotes }: { quotes: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        Worth remembering
      </p>
      <ul className="flex flex-col gap-2">
        {quotes.map((quote, idx) => (
          <li
            key={idx}
            className="flex gap-2 border-l-2 border-[rgba(201,168,76,0.4)] pl-3 text-[14px] italic leading-relaxed text-[rgba(255,255,255,0.75)]"
          >
            <Quote className="mt-0.5 h-3 w-3 shrink-0 text-[rgba(201,168,76,0.5)]" />
            <span>{quote}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChipRow({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        {label}
      </span>
      {values.map((value, idx) => (
        <span
          key={idx}
          className="rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-2.5 py-0.5 text-[12px] text-[rgba(255,255,255,0.75)]"
        >
          {value}
        </span>
      ))}
    </div>
  );
}

// ── Expandable transcript showing every speaker + per-turn Play ──

interface TranscriptBlockProps {
  sessionId: string;
  contacts: Contact[];
  /** Cached turns lifted into the parent so unknown-speaker count can
   * share one fetch with the transcript render. */
  turns: ProcessedTurn[] | null;
  onTurnsLoaded: (turns: ProcessedTurn[]) => void;
}

function TranscriptBlock({
  sessionId,
  contacts,
  turns,
  onTurnsLoaded,
}: TranscriptBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTurns = useCallback(async () => {
    if (turns !== null) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await listTurnsForSession(sessionId);
      fetched.sort((a, b) => a.startSeconds - b.startSeconds);
      onTurnsLoaded(fetched);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId, turns, onTurnsLoaded]);

  // Load turns on mount so the header's unknown-speaker count has
  // data even before the transcript section is expanded. Expanding
  // just changes what's visible, not what's fetched.
  useEffect(() => {
    void loadTurns();
  }, [loadTurns]);

  // Map each unique contact_id to a stable accent color so the
  // transcript reads like a conversation. user_self = neutral,
  // the rest rotate through a small palette so ≤4 people stay
  // visually distinct.
  const colorForContact = useMemo(() => {
    const accents = [
      "text-[rgba(201,168,76,0.85)]",      // amber
      "text-[rgba(120,180,255,0.85)]",     // blue
      "text-[rgba(180,120,220,0.85)]",     // purple
      "text-[rgba(120,200,150,0.85)]",     // green
      "text-[rgba(255,140,120,0.85)]",     // coral
    ];
    const map = new Map<string, string>();
    let cursor = 0;
    return (contactId: string | null): string => {
      if (contactId === "user_self") return "text-[rgba(255,255,255,0.7)]";
      if (contactId === null) return "text-[rgba(255,255,255,0.35)]";
      const cached = map.get(contactId);
      if (cached) return cached;
      const accent = accents[cursor % accents.length];
      cursor += 1;
      map.set(contactId, accent);
      return accent;
    };
  }, []);

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] px-4 py-3 active:bg-[rgba(255,255,255,0.04)]"
      >
        <span className="text-[13px] font-medium uppercase tracking-[0.15em] text-[rgba(255,255,255,0.55)]">
          Transcript{turns ? ` · ${turns.length} turns` : ""}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[rgba(255,255,255,0.4)] transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="mt-3 flex flex-col gap-3">
          {loading && (
            <p className="text-[13px] text-[rgba(255,255,255,0.4)]">
              Loading transcript…
            </p>
          )}
          {error && (
            <p className="text-[13px] text-[rgba(255,120,120,0.9)]">
              Couldn&rsquo;t load transcript: {error}
            </p>
          )}
          {turns && turns.length === 0 && (
            <p className="text-[13px] text-[rgba(255,255,255,0.4)]">
              No transcript available for this session.
            </p>
          )}
          {turns && turns.length > 0 && (
            <ol className="flex flex-col gap-3">
              {turns.map((turn) => (
                <TranscriptTurn
                  key={turn.id}
                  turn={turn}
                  speakerName={
                    turn.contactId === "user_self"
                      ? "You"
                      : turn.contactId
                        ? displayNameForContactId(turn.contactId, contacts)
                        : (turn.speakerLabel ?? "Unknown voice")
                  }
                  labelClass={colorForContact(turn.contactId)}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

interface TranscriptTurnProps {
  turn: ProcessedTurn;
  speakerName: string;
  labelClass: string;
}

function TranscriptTurn({ turn, speakerName, labelClass }: TranscriptTurnProps) {
  const [playing, setPlaying] = useState(false);
  const text = turn.transcript?.trim();

  const handlePlay = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      await invoke("plugin:voice|play_turn_audio", {
        sessionId: turn.sessionId,
        startSeconds: turn.startSeconds,
        endSeconds: turn.endSeconds,
      });
    } catch (err) {
      console.warn("Play turn failed:", err);
    } finally {
      setPlaying(false);
    }
  };

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className={`text-[12px] font-medium truncate ${labelClass}`}>
            {speakerName}
          </span>
          <span className="font-mono text-[10px] text-[rgba(255,255,255,0.3)]">
            {formatTimestamp(turn.startSeconds)}
          </span>
        </div>
        <button
          type="button"
          onClick={handlePlay}
          disabled={playing}
          aria-label="Play this turn"
          className="flex shrink-0 items-center gap-1 rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] px-2 py-0.5 text-[10px] text-[rgba(255,255,255,0.6)] active:scale-95 disabled:opacity-50"
        >
          {playing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Play className="h-3 w-3" />
          )}
        </button>
      </div>
      <p className="text-[14px] leading-relaxed text-[rgba(255,255,255,0.85)]">
        {text || (
          <span className="italic text-[rgba(255,255,255,0.3)]">
            (no transcript for this turn)
          </span>
        )}
      </p>
    </li>
  );
}

function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

// ── Location ──

interface LocationSectionProps {
  latitude: number;
  longitude: number;
  label: string | null;
}

function LocationSection({ latitude, longitude, label }: LocationSectionProps) {
  const appleMapsUrl = `https://maps.apple.com/?ll=${latitude.toFixed(
    6
  )},${longitude.toFixed(6)}&q=${encodeURIComponent(label ?? "Interaction")}`;

  return (
    <section>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
        Where
      </p>
      <div className="mt-2 overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.08)]">
        <LocationMap latitude={latitude} longitude={longitude} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[12px]">
        <span className="text-muted-foreground font-mono">
          {latitude.toFixed(5)}, {longitude.toFixed(5)}
        </span>
        <a
          href={appleMapsUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[rgba(201,168,76,0.85)] active:opacity-70"
        >
          Open in Maps
        </a>
      </div>
    </section>
  );
}

function NoLocationSection() {
  return (
    <section>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
        Where
      </p>
      <div className="mt-2 flex items-center gap-2 rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] px-4 py-3 text-[13px] text-[rgba(255,255,255,0.5)]">
        <MapPinOff className="h-4 w-4" />
        No location captured
      </div>
    </section>
  );
}
